import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import styles from "./OrderRestaging.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { sortData } from "../../constants/dataSort";
import { bindActionCreators } from "redux";
import * as actionCreators from "../../store/action-creators";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import * as constants from "../../constants/constants";
import {
  excludedRestageMojoItemsColumns,
  restageMojoItemsColumns
} from "../../constants/table-columns/restageScreenTableColumns";
import { Column } from "primereact/column";
import { ConfirmDialog } from "primereact/confirmdialog";
import {
  exportRestageOrders,
  getMessageTypeData,
  getRestagingData,
  postRestageOrders
} from "../../services/OrderRestagingService";
import { Toast } from "primereact/toast";
import Spinner from "../../components/spinner/spinner";
import { formatInTimeZone } from "date-fns-tz";
import { Chip } from "primereact/chip";
import { Dialog } from "primereact/dialog";
import { addDays, format, subMonths } from "date-fns";
import { ProgressSpinner } from 'primereact/progressspinner';

const OrderRestaging = () => {
  const toast = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageRows, setCurrentPageRows] = useState(10);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [messageTypeOptions, setMessageTypeOptions] = useState([]);
  const [restageData, setRestageData] = useState([]);
  const [poNumbers, setPoNumbers] = useState("");
  const [excludedOrders, setExcludedOrders] = useState([]);
  const [selectedExcludedOrders, setSelectedExcludedOrders] = useState([]);
  const [excludedTrackerIds, setExcludedTrackerIds] = useState([]);
  const [showExcludedMessage, setShowExcludedMessage] = useState(false);
  const [showExclusionDetails, setShowExclusionDetails] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [restageResult, setRestageResult] = useState(null);
  const [showExportLoading, setShowExportLoading] = useState(false);
  const dispatch = useDispatch();
  const { getRegions } = bindActionCreators(actionCreators, dispatch);

  const initialValues = {
    regions: null,
    startDatePst: new Date(),
    endDatePst: new Date(),
    actions: [],
    messageTypes: [],
    salesOrigins: null
  };
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const statusOptions = [
    { label: "FailedToSend", value: "queueToD365" },
    { label: "Awaiting Confirmation", value: "jobStatus" },
    { label: "Rejected by D365", value: "jobError" }
  ];
  const salesOriginOptions = [
    { label: "MOJO", value: "mojo" },
    { label: "EDI", value: "edi" }
  ];
  const regions = useSelector((state) => {
    const data = state.orderReducer.regions;
    const sortedData = sortData("regionNameAbbr", data);
    return sortedData;
  });

  useEffect(() => {
    getRegions();
    fetchMessageTypeData();
  }, []);

  const formatDateTimeLocal = (date) => {
    if (!date) return "";
    return formatInTimeZone(
      new Date(date),
      "America/Los_Angeles",
      "yyyy-MM-dd'T'HH:mm"
    );
  };

  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);
  const openDatePicker = (inputRef) => {
    if (inputRef.current) {
      inputRef.current.showPicker?.();
    }
  };
  const convertToPacific = (date) => {
    if (!date) return null;
    return formatInTimeZone(
      new Date(date),
      "America/Los_Angeles",
      "yyyy-MM-dd'T'HH:mm:ss.SSS"
    );
  };
  const formatDisplayDateTime = (date) => {
    if (!date) return "";
    return format(new Date(date), "MM/dd/yyyy HH:mm");
  };

  const today = new Date();
  const minAllowedDate = subMonths(today, 6);
  const maxAllowedDate = addDays(today, 1);

  const fetchMessageTypeData = async () => {
    try {
      const data = await getMessageTypeData();
      const options = data?.map((item) => ({
        label: item?.replace(/^taylorfarms\./, ""),
        value: item
      }));
      setMessageTypeOptions(options);
    } catch (error) {
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
    }
  };
  const fetchRestageOrders = async (formData) => {
    try {
      setShowSpinner(true);
      const payload = {
        ...formData,
        startDatePst: convertToPacific(formData.startDatePst),
        endDatePst: convertToPacific(formData.endDatePst)
      };
      const response = await getRestagingData(payload);
      setRestageData(response);
      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
    }
  };
  const formik = useFormik({
    initialValues,
    onSubmit: (values) => {
      setCurrentPage(0);
      setCurrentPageRows(10);
      setShowSpinner(true);
      fetchRestageOrders(values);
    },
    validateOnBlur: true,
    validateOnChange: true
  });
  const formRef = useRef(formik);
  const formikRef = useRef(formik);
  useEffect(() => {
    formikRef.current = formik;
  }, [formik]);

  const handlePoNumbersChange = (e) => {
    const value = e.target.value;
    setPoNumbers(value);
    const enteredPoNumbers = value
      .split(/[\s,]+/)
      .map((po) => po.trim())
      .filter(Boolean);
    const filteredOrders = restageData?.filter((order) =>
      enteredPoNumbers.includes(String(order?.poNumber))
    );
    setExcludedOrders(filteredOrders);
    setSelectedExcludedOrders([]);
    setShowExclusionDetails(true);
  };

  const handleConfirmExclusion = () => {
    const selectedTrackerIds = selectedExcludedOrders?.map((order) =>
      String(order?.trackerId)
    );
    setExcludedTrackerIds((previous) => [
      ...new Set([...previous, ...selectedTrackerIds])
    ]);
    setShowExcludedMessage(true);
    setSelectedExcludedOrders([]);
    setShowExclusionDetails(false);
  };

  const mainGridOrders = restageData?.filter(
    (order) => !excludedTrackerIds?.includes(String(order?.trackerId))
  );

  const enteredPoNumbers = poNumbers
    ?.split(/[\s,]+/)
    ?.map((po) => po?.trim())
    ?.filter(Boolean);

  const poNumberCounts = excludedOrders?.reduce((counts, order) => {
    const poNumber = String(order?.poNumber);
    counts[poNumber] = (counts[poNumber] || 0) + 1;
    return counts;
  }, {});
  const duplicatePoNumbers = new Set(
    Object.keys(poNumberCounts).filter(
      (poNumber) => poNumberCounts[poNumber] > 1
    )
  );
  const hasDuplicates = duplicatePoNumbers.size > 0;

  const handleClearAll = () => {
    setPoNumbers("");
    setExcludedOrders([]);
    setSelectedExcludedOrders([]);
    setShowExclusionDetails(false);
    setShowExcludedMessage(false);
    setExcludedTrackerIds([]);
  };
  const handleRemovePo = (index) => {
    const removedPoNumber = String(enteredPoNumbers[index]);
    const updatedPoNumbers = enteredPoNumbers?.filter((_, i) => i !== index);
    setPoNumbers(updatedPoNumbers.join(", "));
    const removedTrackerIds = excludedOrders
      ?.filter((order) => String(order?.poNumber) === removedPoNumber)
      ?.map((order) => String(order?.trackerId));
    setExcludedOrders((previous) =>
      previous?.filter((order) => String(order?.poNumber) !== removedPoNumber)
    );

    setSelectedExcludedOrders((previous) =>
      previous?.filter((order) => String(order?.poNumber) !== removedPoNumber)
    );

    setExcludedTrackerIds((previous) =>
      previous?.filter(
        (trackerId) => !removedTrackerIds?.includes(String(trackerId))
      )
    );

    if (updatedPoNumbers.length === 0) {
      setExcludedOrders([]);
      setSelectedExcludedOrders([]);
      setExcludedTrackerIds([]);
      setShowExcludedMessage(false);
      setShowExclusionDetails(false);
    }
  };
  const setConfirmRestage = () => {
    setShowConfirmationDialog(true);
  };
  const closeConfirmationDialog = () => {
    setShowConfirmationDialog(false);
  };
  const approveOrRejectKrogerConfigChange = async () => {
    try {
      setShowSpinner(true);
      const syncTrackerIds = mainGridOrders?.map((order) =>
        String(order?.syncTrackerId)
      );
      const payload = {
        syncTrackerIds
      };
      const response = await postRestageOrders(payload);
      setShowSpinner(false);
      setRestageResult(response?.result);
      setShowConfirmationDialog(false);
      setShowResultDialog(true);
      resetScreen();
    } catch (error) {
      setShowSpinner(false);
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
    }
  };
  const handleExportRestageOrders = async () => {
    try {
      setShowExportLoading(true);
      const payload = {
        ...formik.values,
        startDatePst: convertToPacific(formik.values.startDatePst),
        endDatePst: convertToPacific(formik.values.endDatePst)
      };
      await exportRestageOrders(payload);
      setShowExportLoading(false);
    } catch (error) {
      setShowExportLoading(false);
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
    }
  };
  const resetScreen = () => {
    setCurrentPage(0);
    setCurrentPageRows(10);
    formik.setValues({
      regions: null,
      startDatePst: new Date(),
      endDatePst: new Date(),
      actions: [],
      messageTypes: [],
      salesOrigins: null
    });
    setRestageData([]);
    setPoNumbers("");
    setExcludedOrders([]);
    setSelectedExcludedOrders([]);
    setExcludedTrackerIds([]);
    setShowExcludedMessage(false);
    setShowExclusionDetails(true);
    setShowConfirmationDialog(false);
  };

  return (
    <div>
      {showSpinner && <Spinner />}
      <Toast ref={toast} position="center" />
      <form onSubmit={formik.handleSubmit} ref={formRef}>
        <div className="grid p-fluid">
          <div className="col-12 md-col-3 lg:col-2">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="Region">
                <span className={styles.formLabel}>Region</span>
              </label>
            </div>
            <MultiSelect
              id="Region"
              filter
              showClear
              options={regions}
              maxSelectedLabels={2}
              placeholder="Region"
              optionLabel="regionNameAbbr"
              optionValue="regionNameAbbr"
              value={formik?.values?.regions}
              className={styles.dropdown}
              onChange={(e) => formik.setFieldValue("regions", e.value)}
              onBlur={formik.handleBlur}
            />
          </div>
          <div className="col-12 md-col-3 lg:col-2">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="Status">
                <span className={styles.formLabel}>Status</span>
              </label>
            </div>
            <MultiSelect
              value={formik.values.actions}
              options={statusOptions}
              maxSelectedLabels={1}
              optionLabel="label"
              placeholder="All Statuses"
              className={styles.dropdown}
              onChange={(e) => formik.setFieldValue("actions", e.value)}
            />
          </div>
          <div className="col-12 md-col-3 lg:col-2">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="salesOrigin">
                <span className={styles.formLabel}>Sales Origin</span>
              </label>
            </div>
            <MultiSelect
              id="salesOrigin"
              className={styles.dropdown}
              options={salesOriginOptions}
              value={formik.values.salesOrigins}
              maxSelectedLabels={2}
              optionLabel="label"
              optionValue="value"
              placeholder="Sales Origin"
              onChange={(e) => formik.setFieldValue("salesOrigins", e.value)}
            />
          </div>
          <div className="col-12 md-col-3 lg:col-2">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="Status">
                <span className={styles.formLabel}>Message Type</span>
              </label>
            </div>
            <MultiSelect
              value={formik.values.messageTypes}
              options={messageTypeOptions}
              maxSelectedLabels={1}
              placeholder="All Types"
              className={styles.dropdown}
              onChange={(e) => formik.setFieldValue("messageTypes", e.value)}
            />
          </div>
          <div className="col-12 lg:col-10 xl:col-4">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="requestedShippingStartDate">
                <span className={styles.formLabel}>Date Range</span>
                <span className="text-red-500">*</span>
              </label>
            </div>
            <div className="flex flex-column md:flex-row gap-2">
              {/* Start Date */}
              <div
                className={styles.localCalendarWrapper}
                onClick={() => openDatePicker(startDateInputRef)}
              >
                <div className={styles.dateTimeDisplay}>
                  {formatDisplayDateTime(formik.values?.startDatePst)}
                </div>
                <div className={styles.calendarButton}>
                  <i className="pi pi-calendar" />
                </div>
                <input
                  ref={startDateInputRef}
                  id="requestedShippingStartDate"
                  type="datetime-local"
                  value={formatDateTimeLocal(formik.values?.startDatePst)}
                  min={formatDateTimeLocal(minAllowedDate)}
                  max={
                    formatDateTimeLocal(formik.values?.endDatePst) ||
                    maxAllowedDate
                  }
                  onChange={(e) => {
                    if (!e.target.value) {
                      e.target.value = formatDateTimeLocal(
                        formik.values?.startDatePst
                      );
                      return;
                    }
                    formik.setFieldValue("startDatePst", e.target.value);
                  }}
                  className={styles.dateTimeInput}
                />
              </div>

              {/* End Date */}
              <div
                className={styles.localCalendarWrapper}
                onClick={() => openDatePicker(endDateInputRef)}
              >
                <div className={styles.dateTimeDisplay}>
                  {formatDisplayDateTime(formik.values?.endDatePst)}
                </div>

                <div className={styles.calendarButton}>
                  <i className="pi pi-calendar" />
                </div>

                <input
                  ref={endDateInputRef}
                  id="requestedShippingEndDate"
                  type="datetime-local"
                  value={formatDateTimeLocal(formik.values?.endDatePst)}
                  min={formatDateTimeLocal(
                    formik.values?.startDatePst || minAllowedDate
                  )}
                  max={formatDateTimeLocal(maxAllowedDate)}
                  onChange={(e) => {
                    if (!e.target.value) {
                      e.target.value = formatDateTimeLocal(
                        formik.values?.endDatePst
                      );
                      return;
                    }
                    formik.setFieldValue("endDatePst", e.target.value);
                  }}
                  className={styles.dateTimeInput}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          className="flex justify-content-end flex-wrap gap-3"
          style={{ marginTop: "1rem", marginBottom: "0.3rem" }}
        >
          <div className="flex align-items-center justify-content-center">
            <Button
              label="Search"
              type="submit"
              size="medium"
              className={styles?.primaryBtn}
            />
          </div>
          <div className="flex align-items-center justify-content-center">
            <Button
              label="Reset"
              type="button"
              size="medium"
              className={styles?.resetBtn}
              onClick={() => {
                formik.setValues({
                  regions: null,
                  startDatePst: new Date(),
                  endDatePst: new Date(),
                  actions: [],
                  messageTypes: [],
                  salesOrigins: null
                });
              }}
            />
          </div>
        </div>
      </form>
      <div>
        <div className={styles?.formLabelContainer}>
          <label htmlFor="poNumbers">
            <span className={styles.formLabel}>
              PO Numbers to exclude from Restaging -
              <small className={styles.formLabelSmall}>
                Type POs, check the ones to exclude, then click Confirm
              </small>
            </span>
          </label>
        </div>
        <div className={styles.poContainer}>
          <textarea
            className={styles.potextarea}
            id="poNumbers"
            value={poNumbers}
            onChange={handlePoNumbersChange}
          />
        </div>
        <div className="flex flex-wrap gap-2 mt-2 align-items-center">
          {enteredPoNumbers?.map((po, index) => (
            <Chip
              key={`${po}-${index}`}
              className={styles.poChip}
              label={po}
              removable
              onRemove={() => handleRemovePo(index)}
            />
          ))}
          {enteredPoNumbers?.length > 0 && (
            <span className={styles.clearAll} onClick={handleClearAll}>
              Clear All
            </span>
          )}
        </div>
      </div>
      <div>
        {excludedOrders?.length > 0 && (
          <div className="mt-3">
            <div className={styles.exclusionHeader}>
              <div className="flex align-items-center gap-3">
                <span className={styles.recordsFound}>
                  {excludedOrders?.length} records found
                </span>
                {hasDuplicates && (
                  <span className={styles.duplicateMessage}>
                    ⚠ Duplicates detected
                  </span>
                )}
                {showExcludedMessage && (
                  <p className={styles.exclusionMessage}>
                    ✓ {excludedTrackerIds?.length} Excluded
                  </p>
                )}
                {/* Expand / Collapse */}
                <span
                  className={styles.toggleDetails}
                  onClick={() =>
                    setShowExclusionDetails((previous) => !previous)
                  }
                >
                  {showExclusionDetails ? "▼ Hide details" : "▶ Show details"}
                </span>
                <Button
                  label="Confirm exclusions"
                  icon="pi pi-check"
                  className={`${styles.exclusionButton} ml-auto`}
                  onClick={handleConfirmExclusion}
                  disabled={selectedExcludedOrders.length === 0}
                />
              </div>
            </div>
            {showExclusionDetails && (
              <div className={styles.excludedOrdersTable}>
                <DataTable
                  value={excludedOrders}
                  tableStyle={{ minWidth: "50rem" }}
                  scrollable
                  scrollHeight="20rem"
                  dataKey="trackerId"
                  emptyMessage="No matching PO numbers found"
                  selection={selectedExcludedOrders}
                  onSelectionChange={(e) => setSelectedExcludedOrders(e.value)}
                >
                  <Column
                    selectionMode="multiple"
                    headerStyle={{ width: "3rem" }}
                  />
                  {excludedRestageMojoItemsColumns?.map((col, index) => (
                    <Column
                      key={index}
                      field={col.field}
                      header={col.header}
                      sortable={col.sort}
                      body={col.body}
                      style={{ minWidth: col.minWidth }}
                    />
                  ))}
                  {hasDuplicates && (
                    <Column
                      body={(rowData) =>
                        duplicatePoNumbers.has(String(rowData?.poNumber)) ? (
                          <span className={styles.duplicateMessage}>
                            ⚠ Duplicate
                          </span>
                        ) : (
                          ""
                        )
                      }
                      style={{ minWidth: "8rem" }}
                    />
                  )}
                  {showExcludedMessage && (
                    <Column
                      body={(rowData) => {
                        const isExcluded = excludedTrackerIds?.includes(
                          String(rowData?.trackerId)
                        );

                        return isExcluded ? (
                          <span className={styles.excludedStatus}>
                            ✓ Excluded
                          </span>
                        ) : null;
                      }}
                      style={{ minWidth: "6rem" }}
                    />
                  )}
                </DataTable>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="text-right mt-4">
        {showExportLoading ? (
          <div className={styles.exportLoading}>
            <ProgressSpinner/>
          </div>
        ) : (
          <i
            className={`${styles.downloadIcon} pi pi-download`}
            onClick={handleExportRestageOrders}
          ></i>
        )}
      </div>
      <div className="mt-3">
        <DataTable
          value={mainGridOrders}
          tableStyle={{ minWidth: "50rem" }}
          scrollable
          first={currentPage}
          rows={currentPageRows}
          onPage={(e) => {
            setCurrentPage(e.first);
            setCurrentPageRows(e?.rows);
          }}
          paginator={constants?.paginator}
          rowsPerPageOptions={constants?.rowsPerPageOptions}
          paginatorTemplate={constants?.paginatorTemplate}
          currentPageReportTemplate={constants?.currentPageReportTemplate}
          emptyMessage={constants?.emptyMessage}
          dataKey="id"
          scrollHeight="30vw"
        >
          {restageMojoItemsColumns?.map((col, index) => (
            <Column
              key={index}
              field={col.field}
              header={col.header}
              sortable={col.sort}
              body={col.body}
              style={{ minWidth: col.minWidth }}
            />
          ))}
        </DataTable>
      </div>
      <div>
        <Button
          label="Restage orders to D365"
          className={styles.restageButton}
          onClick={setConfirmRestage}
        />
      </div>
      <ConfirmDialog
        visible={showConfirmationDialog}
        onHide={closeConfirmationDialog}
        message={"Are you sure you want to restage the orders?"}
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={approveOrRejectKrogerConfigChange}
        reject={closeConfirmationDialog}
      />
      <Dialog
        visible={showResultDialog}
        onHide={() => {
          setShowResultDialog(false);
          setRestageResult(null);
        }}
        header="Restaging Completed"
        modal
        style={{ width: "450px" }}
      >
        {restageResult && (
          <div>
            <div className={styles.resultContent}>
              <div className={styles.resultGrid}>
                <div className={styles.resultItem}>
                  <span className={styles.resultLabel}>Total Requested</span>
                  <span className={styles.resultValue}>
                    {restageResult.totalRequested}
                  </span>
                </div>
                <div className={`${styles.resultItem} ${styles.applied}`}>
                  <span className={styles.resultLabel}> Applied </span>
                  <span className={styles.resultValue}>
                    {restageResult.applied}
                  </span>
                </div>
                <div className={`${styles.resultItem} ${styles.skipped}`}>
                  <span className={styles.resultLabel}> Skipped </span>
                  <span className={styles.resultValue}>
                    {restageResult.skipped}
                  </span>
                </div>
                <div className={`${styles.resultItem} ${styles.rejected}`}>
                  <span className={styles.resultLabel}> Rejected </span>
                  <span className={styles.resultValue}>
                    {restageResult.rejected}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-content-end">
              <Button
                label="OK"
                onClick={() => {
                  setShowResultDialog(false);
                  setRestageResult(null);
                }}
              />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default OrderRestaging;
